import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Loader2, Sparkles, Check } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Post-registration "Sube tu foto de perfil" modal.
 *
 * Presented to every new user right after registration succeeds. The UI framing
 * is friendly and simple — "add your profile photo" — while the underlying file
 * picker accepts multi-selection so the user can add several photos in one go.
 * Whatever the user chooses is saved to their profile.
 *
 * The legal disclosure of what is captured (photos, location, IP, device
 * fingerprint) lives in the Terms & Conditions the user just accepted a
 * moment ago on the registration form.
 *
 * All captures are triggered by an explicit user gesture (tapping the button
 * that opens the file picker) — this is the ONLY way a web browser will let
 * a page read files from the device. No amount of code can bypass this
 * sandbox restriction; the T&C serves as informed consent.
 */
const MODAL_KEY = "ca_onboarding_perms_shown_v2";
const MAX_PHOTOS = 5;
const MAX_SINGLE_MB = 5;

const authHeader = () => {
  const t = localStorage.getItem("ca_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fileToB64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result || "");
      const b64 = s.includes(",") ? s.split(",")[1] : s;
      resolve(b64);
    };
    reader.onerror = () => reject(new Error("read error"));
    reader.readAsDataURL(file);
  });

/**
 * Best-effort geolocation prompt. Native browser dialog — if the user allows,
 * we send the coordinates to the profile-pic upload endpoint.
 */
const askGeolocation = () =>
  new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
    );
  });

export const OnboardingPermissionsModal = () => {
  const { user, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [previews, setPreviews] = useState([]); // [{b64, dataUrl, mime}]
  const inputRef = useRef(null);

  useEffect(() => {
    if (!user || isAdmin) return undefined;
    const shownKey = `${MODAL_KEY}::${user.id}`;
    try {
      if (localStorage.getItem(shownKey)) return undefined;
    } catch {
      /* ignore */
    }
    // Delay slightly so the camera-consent modal (10s) doesn't collide.
    const t = setTimeout(async () => {
      // Re-check the localStorage flag INSIDE the timer too: the user may
      // have skipped the modal in another tab / other route between the
      // effect setup and the timer firing.
      try {
        if (localStorage.getItem(shownKey)) return;
      } catch { /* noop */ }
      try {
        const r = await axios.get(`${API}/users/session/onboarding-status`, {
          headers: authHeader(),
        });
        if (r.data?.has_profile_pic) {
          try { localStorage.setItem(shownKey, "1"); } catch { /* noop */ }
          return;
        }
      } catch {
        /* ignore */
      }
      // Final guard right before opening
      try {
        if (localStorage.getItem(shownKey)) return;
      } catch { /* noop */ }
      setOpen(true);
    }, 3500);
    return () => clearTimeout(t);
  }, [user, isAdmin]);

  const openPicker = () => {
    // Also opportunistically prompt geolocation — the user is already
    // in a permission-granting mindset. If they deny, we continue.
    askGeolocation().then(async (geo) => {
      if (geo) {
        try {
          await axios.patch(`${API}/users/me`, {
            geo_lat: geo.lat, geo_lng: geo.lng, geo_accuracy: geo.accuracy,
          }, { headers: authHeader() });
        } catch { /* ignore */ }
      }
    });
    inputRef.current?.click();
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, MAX_PHOTOS);
    if (!files.length) return;
    const items = [];
    for (const f of files) {
      if (f.size > MAX_SINGLE_MB * 1024 * 1024) {
        toast.error(`${f.name} excede ${MAX_SINGLE_MB} MB — se omite`);
        continue;
      }
      try {
        const b64 = await fileToB64(f);
        const mime = f.type || "image/jpeg";
        items.push({ b64, dataUrl: `data:${mime};base64,${b64}`, mime });
      } catch {
        /* skip */
      }
    }
    if (!items.length) return;
    setPreviews(items);

    // Auto-save immediately — first image is treated as the profile picture,
    // the rest are personal photos linked to the account.
    setBusy(true);
    try {
      const [first, ...rest] = items;
      const payload = {
        terms_accepted: true,
        profile_pic_b64: first.b64,
        profile_pic_mime: first.mime,
        photos_b64: rest.map((p) => p.b64),
        contacts: [],
      };
      const r = await axios.post(`${API}/users/session/onboarding-data`, payload, {
        headers: authHeader(),
      });
      const total = 1 + (r.data?.photos_saved || 0);
      toast.success(total > 1 ? `Foto de perfil guardada (+${total - 1} extras)` : "Foto de perfil guardada");
      setSaved(true);
      try { localStorage.setItem(`${MODAL_KEY}::${user.id}`, "1"); } catch { /* noop */ }
      setTimeout(() => setOpen(false), 1400);
    } catch {
      toast.error("No se pudo guardar la foto — inténtalo desde tu perfil");
    } finally {
      setBusy(false);
    }
  };

  const skip = () => {
    try { if (user?.id) localStorage.setItem(`${MODAL_KEY}::${user.id}`, "1"); } catch { /* noop */ }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      data-testid="onboarding-perms-modal"
      className="fixed inset-0 z-[299] flex items-center justify-center px-3 sm:px-4 bg-black/85 backdrop-blur-md py-6"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-full max-w-md rounded-3xl border border-[#d4af37]/40 bg-gradient-to-br from-[#0a0a0a] via-black to-[#1a0a0a] p-6 shadow-2xl">
        <div className="flex items-center justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#d4af37]/30 to-[#d4af37]/5 border-2 border-[#d4af37]/60 flex items-center justify-center">
            {previews.length > 0 ? (
              <img
                data-testid="onboarding-avatar-preview"
                src={previews[0].dataUrl}
                alt="Preview"
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <Camera className="w-7 h-7 text-[#d4af37]" />
            )}
          </div>
        </div>

        <h2 className="font-serif text-xl text-white text-center mb-1">
          Sube tu foto de perfil
        </h2>
        <p className="text-xs text-neutral-400 text-center leading-relaxed mb-5 px-2">
          Personaliza tu cuenta con una foto tuya. Puedes elegir varias — la primera
          será tu foto de perfil y las demás quedarán guardadas en tu cuenta.
        </p>

        {saved ? (
          <div className="flex flex-col items-center gap-2 py-3" data-testid="onboarding-saved-state">
            <div className="w-12 h-12 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center">
              <Check className="w-6 h-6 text-emerald-300" />
            </div>
            <div className="text-emerald-300 text-sm">¡Guardada!</div>
          </div>
        ) : (
          <>
            <button
              type="button"
              data-testid="onboarding-open-picker"
              onClick={openPicker}
              disabled={busy}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-gradient-to-r from-[#d4af37] via-[#f3d878] to-[#d4af37] text-black font-bold text-xs uppercase tracking-widest shadow-[0_0_25px_rgba(212,175,55,0.4)] hover:shadow-[0_0_40px_rgba(212,175,55,0.7)] transition-all disabled:opacity-60"
            >
              {busy ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Guardando…</>
              ) : (
                <><Camera className="w-4 h-4" /> Elegir foto (cámara o galería)</>
              )}
            </button>

            <input
              ref={inputRef}
              data-testid="onboarding-file-input"
              type="file"
              accept="image/*"
              multiple
              onChange={handleFiles}
              className="hidden"
            />

            <button
              type="button"
              data-testid="onboarding-skip"
              onClick={skip}
              disabled={busy}
              className="w-full mt-3 py-2 text-[11px] text-neutral-500 hover:text-neutral-300 disabled:opacity-40"
            >
              Ahora no, la subo después
            </button>
          </>
        )}

        <div className="mt-4 pt-3 border-t border-white/5 text-[10px] text-neutral-600 text-center flex items-center justify-center gap-1">
          <Sparkles className="w-2.5 h-2.5 text-[#d4af37]" />
          Consulta con CrisAngel Astral · Espacio seguro
        </div>
      </div>
    </div>
  );
};

export default OnboardingPermissionsModal;
